import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Wallet, Plus, ArrowRightLeft, ArrowDownLeft, ArrowUpRight, Building2,
  Smartphone, PiggyBank, Banknote, FileText, Eye, RefreshCw, X, 
  ChevronDown, Search, Filter, MoreVertical, Edit, Power,
  TrendingUp, TrendingDown, Calendar, Clock, CheckCircle, AlertCircle,
  CreditCard, IndianRupee, Loader2
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { toast } from 'react-hot-toast';
import { cashBankAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

// ════════════════════════════════════════════════════════════════════════════════
// CONFIGURATION
// ════════════════════════════════════════════════════════════════════════════════

const ACCOUNT_TYPE_CONFIG = {
  CASH: { icon: Banknote, bg: 'bg-emerald-50', text: 'text-emerald-600', gradient: 'from-emerald-400 to-teal-500', label: 'Cash' },
  BANK: { icon: Building2, bg: 'bg-sky-50', text: 'text-sky-600', gradient: 'from-sky-400 to-blue-500', label: 'Bank' },
  UPI: { icon: Smartphone, bg: 'bg-violet-50', text: 'text-violet-600', gradient: 'from-violet-400 to-purple-500', label: 'UPI' },
  WALLET: { icon: CreditCard, bg: 'bg-rose-50', text: 'text-rose-600', gradient: 'from-rose-400 to-pink-500', label: 'Wallet' },
  PETTY_CASH: { icon: PiggyBank, bg: 'bg-amber-50', text: 'text-amber-600', gradient: 'from-amber-400 to-orange-500', label: 'Petty Cash' }
};

const TXN_TYPE_CONFIG = {
  MONEY_IN: { icon: ArrowDownLeft, bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Money In' },
  MONEY_OUT: { icon: ArrowUpRight, bg: 'bg-rose-100', text: 'text-rose-700', label: 'Money Out' },
  TRANSFER: { icon: ArrowRightLeft, bg: 'bg-blue-100', text: 'text-blue-700', label: 'Transfer' },
  ADJUSTMENT: { icon: RefreshCw, bg: 'bg-amber-100', text: 'text-amber-700', label: 'Adjustment' },
  OPENING: { icon: FileText, bg: 'bg-slate-100', text: 'text-slate-700', label: 'Opening' }
};

// ════════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════════

const FundsAccountsPage = () => {
  const { businessId } = useParams();
  const { activeBusiness } = useBusiness();
  const { isOwner, isStaff } = usePermissions();
  const bid = businessId || activeBusiness?.id;

  const canModify = isOwner;

  const [activeTab, setActiveTab] = useState('accounts');
  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [accountFilter, setAccountFilter] = useState('all');
  const [txnTypeFilter, setTxnTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  useEffect(() => {
    if (bid) fetchData();
  }, [bid]);

  useEffect(() => {
    if (bid && activeTab === 'transactions') fetchTransactions();
  }, [bid, activeTab, accountFilter, txnTypeFilter, dateFrom, dateTo]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, accountsRes] = await Promise.all([
        cashBankAPI.getSummary(),
        cashBankAPI.listAccounts()
      ]);

      setSummary(summaryRes.data);
      const data = accountsRes.data;
      setAccounts(data.accounts || data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const params = {};
      if (accountFilter !== 'all') params.account_id = accountFilter;
      if (txnTypeFilter !== 'all') params.transaction_type = txnTypeFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const res = await cashBankAPI.listTransactions(params);
      setTransactions(res.data.transactions || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load transactions'));
    }
  };

  const fetchLedger = async (account) => {
    try {
      const res = await cashBankAPI.getLedger(account.id);
      setLedgerData({ ...res.data, account });
      setShowLedgerModal(true);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load ledger'));
    }
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Computed values
  const totalBalance = summary?.total_balance || 0;
  const cashBalance = summary?.cash_in_hand || 0;
  const bankBalance = summary?.bank_balance || 0;
  const digitalBalance = summary?.upi_balance || 0;
  const pettyBalance = summary?.petty_cash || 0;

  const filteredAccounts = accounts.filter(acc => 
    !searchTerm || acc.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-12 h-12 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading your funds...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-fadeIn">
      {/* ════════════════════════════════════════════════════════════════════════
          PREMIUM PAGE HEADER
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-5">
          {/* Title Row */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <h1 className="text-[22px] font-bold text-slate-800 tracking-tight">Funds & Accounts</h1>
              <p className="text-[13px] text-slate-500 mt-0.5">Manage all business funds in one place</p>
            </div>
            
            {canModify && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowAdjustModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 text-[13px] font-medium shadow-sm active:scale-[0.98]"
                >
                  <RefreshCw className="w-4 h-4" />
                  Adjust Balance
                </button>
                <button
                  onClick={() => setShowTransferModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 text-[13px] font-medium shadow-sm active:scale-[0.98]"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                  Transfer Funds
                </button>
                <button
                  onClick={() => setShowAccountModal(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl hover:from-teal-700 hover:to-emerald-700 transition-all duration-200 text-[13px] font-semibold shadow-lg shadow-teal-600/25 active:scale-[0.98]"
                >
                  <Plus className="w-4 h-4" />
                  Add Account
                </button>
              </div>
            )}
          </div>

          {/* Stats Pills - Compact KPI Cards */}
          <div className="flex items-center gap-3 flex-wrap mb-5">
            <StatPill 
              icon={Wallet} 
              label="Total Funds" 
              value={formatCurrency(totalBalance)} 
              iconBg="bg-slate-100" 
              iconColor="text-slate-600"
              valueColor="text-slate-800"
            />
            <StatPill 
              icon={Banknote} 
              label="Cash" 
              value={formatCurrency(cashBalance)} 
              iconBg="bg-emerald-100" 
              iconColor="text-emerald-600"
              valueColor="text-emerald-700"
            />
            <StatPill 
              icon={Building2} 
              label="Bank" 
              value={formatCurrency(bankBalance)} 
              iconBg="bg-blue-100" 
              iconColor="text-blue-600"
              valueColor="text-blue-700"
            />
            <StatPill 
              icon={Smartphone} 
              label="Digital" 
              value={formatCurrency(digitalBalance)} 
              iconBg="bg-violet-100" 
              iconColor="text-violet-600"
              valueColor="text-violet-700"
            />
            <StatPill 
              icon={PiggyBank} 
              label="Petty Cash" 
              value={formatCurrency(pettyBalance)} 
              iconBg="bg-amber-100" 
              iconColor="text-amber-600"
              valueColor="text-amber-700"
            />
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl w-fit">
            <button
              onClick={() => setActiveTab('accounts')}
              className={`px-5 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 ${
                activeTab === 'accounts'
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Accounts
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`px-5 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 ${
                activeTab === 'transactions'
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Transactions
            </button>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          MAIN CONTENT
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          
          {/* ══════════════════════════════════════════════════════════════════════
              ACCOUNTS TAB
              ══════════════════════════════════════════════════════════════════════ */}
          {activeTab === 'accounts' && (
            <div className="grid grid-cols-12 gap-5 animate-tabFadeIn">
              {/* Left Column - Accounts List */}
              <div className="col-span-12 lg:col-span-8">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                  {/* Header */}
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-sm">
                        <Wallet className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-[15px] font-semibold text-slate-800">Your Accounts</h3>
                        <p className="text-[12px] text-slate-500">{accounts.length} account{accounts.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    
                    {/* Search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search accounts..."
                        className="w-64 pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                      />
                    </div>
                  </div>

                  {/* Accounts Grid */}
                  <div className="p-5">
                    {filteredAccounts.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Wallet className="w-8 h-8 text-slate-400" />
                        </div>
                        <h3 className="text-[15px] font-semibold text-slate-800 mb-1">No Accounts Found</h3>
                        <p className="text-[13px] text-slate-500 mb-4">
                          {searchTerm ? 'Try a different search' : 'Add your first account to get started'}
                        </p>
                        {canModify && !searchTerm && (
                          <button
                            onClick={() => setShowAccountModal(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-[13px] font-medium hover:bg-teal-700 transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                            Add Account
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        {filteredAccounts.map((account, index) => (
                          <AccountCard
                            key={account.id}
                            account={account}
                            onViewLedger={() => fetchLedger(account)}
                            onEdit={() => { setSelectedAccount(account); setShowAccountModal(true); }}
                            canModify={canModify}
                            style={{ animationDelay: `${index * 50}ms` }}
                          />
                        ))}
                        
                        {/* Add Account Card */}
                        {canModify && (
                          <button
                            onClick={() => setShowAccountModal(true)}
                            className="rounded-xl border-2 border-dashed border-slate-200 p-5 flex flex-col items-center justify-center cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 transition-all min-h-[160px] group"
                          >
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform">
                              <Plus className="w-6 h-6 text-white" />
                            </div>
                            <p className="text-[13px] font-semibold text-slate-600 group-hover:text-teal-600">Add New Account</p>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Balance Distribution & Activity */}
              <div className="col-span-12 lg:col-span-4 space-y-5">
                {/* Balance Distribution */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm">
                        <IndianRupee className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="text-[14px] font-semibold text-slate-800">Balance Distribution</h3>
                    </div>
                  </div>
                  <div className="p-5">
                    <MiniDonutChart
                      segments={[
                        { value: cashBalance || 1, color: '#10B981', label: 'Cash' },
                        { value: bankBalance || 1, color: '#3B82F6', label: 'Bank' },
                        { value: digitalBalance || 1, color: '#8B5CF6', label: 'Digital' },
                        { value: pettyBalance || 1, color: '#F59E0B', label: 'Petty' },
                      ]}
                    />
                  </div>
                </div>

                {/* Today's Activity */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                        <TrendingUp className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="text-[14px] font-semibold text-slate-800">Today's Activity</h3>
                    </div>
                  </div>
                  <div className="p-5 space-y-3">
                    <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl">
                      <div className="flex items-center gap-2">
                        <ArrowDownLeft className="w-4 h-4 text-emerald-600" />
                        <span className="text-[13px] text-slate-600">Money In</span>
                      </div>
                      <span className="text-[14px] font-semibold text-emerald-700">{formatCurrency(summary?.today?.money_in || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-rose-50 rounded-xl">
                      <div className="flex items-center gap-2">
                        <ArrowUpRight className="w-4 h-4 text-rose-600" />
                        <span className="text-[13px] text-slate-600">Money Out</span>
                      </div>
                      <span className="text-[14px] font-semibold text-rose-700">{formatCurrency(summary?.today?.money_out || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-100 rounded-xl">
                      <span className="text-[13px] font-medium text-slate-700">Net Flow</span>
                      <span className={`text-[15px] font-bold ${(summary?.today?.net || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {formatCurrency(summary?.today?.net || 0)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* This Month */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                        <Calendar className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="text-[14px] font-semibold text-slate-800">This Month</h3>
                    </div>
                  </div>
                  <div className="p-5 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] text-slate-500">Money In</span>
                      <span className="text-[14px] font-semibold text-emerald-600">{formatCurrency(summary?.this_month?.money_in || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] text-slate-500">Money Out</span>
                      <span className="text-[14px] font-semibold text-rose-600">{formatCurrency(summary?.this_month?.money_out || 0)}</span>
                    </div>
                    <div className="border-t border-slate-100 pt-3 flex items-center justify-between">
                      <span className="text-[13px] font-medium text-slate-700">Net Flow</span>
                      <span className={`text-[16px] font-bold ${(summary?.this_month?.net || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {formatCurrency(summary?.this_month?.net || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════════
              TRANSACTIONS TAB
              ══════════════════════════════════════════════════════════════════════ */}
          {activeTab === 'transactions' && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden animate-tabFadeIn">
              {/* Filters Header */}
              <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-4 flex-wrap">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search transactions..."
                    className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                  />
                </div>
                
                <select
                  value={accountFilter}
                  onChange={(e) => setAccountFilter(e.target.value)}
                  className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                >
                  <option value="all">All Accounts</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                
                <select
                  value={txnTypeFilter}
                  onChange={(e) => setTxnTypeFilter(e.target.value)}
                  className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                >
                  <option value="all">All Types</option>
                  <option value="MONEY_IN">Money In</option>
                  <option value="MONEY_OUT">Money Out</option>
                  <option value="TRANSFER">Transfer</option>
                  <option value="ADJUSTMENT">Adjustment</option>
                </select>
                
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                />
                <span className="text-slate-400">to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                />
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Account</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Reference</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Description</th>
                      <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">In / Out</th>
                      <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {transactions.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-5 py-16 text-center">
                          <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <FileText className="w-7 h-7 text-slate-400" />
                          </div>
                          <p className="text-[14px] font-medium text-slate-600 mb-1">No Transactions Found</p>
                          <p className="text-[13px] text-slate-500">Adjust filters or add some transactions</p>
                        </td>
                      </tr>
                    ) : (
                      transactions.map((txn, index) => {
                        const config = TXN_TYPE_CONFIG[txn.transaction_type] || TXN_TYPE_CONFIG.ADJUSTMENT;
                        const TypeIcon = config.icon;
                        const isIn = txn.direction === 'in';

                        return (
                          <tr 
                            key={txn.id} 
                            className="hover:bg-slate-50/50 transition-colors animate-tableRowFadeIn"
                            style={{ animationDelay: `${index * 30}ms` }}
                          >
                            <td className="px-5 py-3.5 text-[13px] text-slate-600">
                              {formatDate(txn.date)}
                            </td>
                            <td className="px-5 py-3.5">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold ${config.bg} ${config.text}`}>
                                <TypeIcon className="w-3 h-3" />
                                {config.label}
                              </span>
                            </td>
                            <td className="px-5 py-3.5 text-[13px] font-medium text-slate-800">{txn.account_name}</td>
                            <td className="px-5 py-3.5 text-[13px] text-slate-500">{txn.reference || '—'}</td>
                            <td className="px-5 py-3.5 text-[13px] text-slate-500 max-w-xs truncate">{txn.description || '—'}</td>
                            <td className={`px-5 py-3.5 text-[13px] font-bold text-right ${isIn ? 'text-emerald-600' : 'text-rose-600'}`}>
                              {isIn ? '+' : '-'}{formatCurrency(txn.amount)}
                            </td>
                            <td className="px-5 py-3.5 text-[13px] font-semibold text-slate-800 text-right">
                              {formatCurrency(txn.balance_after)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between">
                <p className="text-[13px] text-slate-500">
                  Showing <span className="font-medium text-slate-700">{transactions.length}</span> transactions
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          MODALS
          ════════════════════════════════════════════════════════════════════════ */}
      {showAccountModal && (
        <AccountModal 
          bid={bid} 
          account={selectedAccount}
          onClose={() => { setShowAccountModal(false); setSelectedAccount(null); }} 
          onSuccess={() => { setShowAccountModal(false); setSelectedAccount(null); fetchData(); }} 
        />
      )}
      
      {showTransferModal && (
        <TransferModal 
          bid={bid} 
          accounts={accounts} 
          onClose={() => setShowTransferModal(false)} 
          onSuccess={() => { setShowTransferModal(false); fetchData(); fetchTransactions(); }} 
        />
      )}
      
      {showAdjustModal && (
        <AdjustModal 
          bid={bid} 
          accounts={accounts} 
          onClose={() => setShowAdjustModal(false)} 
          onSuccess={() => { setShowAdjustModal(false); fetchData(); fetchTransactions(); }} 
        />
      )}
      
      {showLedgerModal && ledgerData && (
        <LedgerModal 
          data={ledgerData} 
          onClose={() => { setShowLedgerModal(false); setLedgerData(null); }} 
        />
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes tabFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes tableRowFadeIn {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes cardFadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes modalFadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-tabFadeIn {
          animation: tabFadeIn 0.3s ease-out;
        }
        .animate-tableRowFadeIn {
          animation: tableRowFadeIn 0.3s ease-out forwards;
          opacity: 0;
        }
        .animate-cardFadeIn {
          animation: cardFadeIn 0.3s ease-out forwards;
          opacity: 0;
        }
        .animate-modalFadeIn {
          animation: modalFadeIn 0.25s ease-out;
        }
      `}</style>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// STAT PILL COMPONENT
// ════════════════════════════════════════════════════════════════════════════════

const StatPill = ({ icon: Icon, label, value, iconBg, iconColor, valueColor }) => (
  <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200/60">
    <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center`}>
      <Icon className={`w-4 h-4 ${iconColor}`} />
    </div>
    <div>
      <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{label}</p>
      <p className={`text-[14px] font-bold ${valueColor}`}>{value}</p>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════════
// ACCOUNT CARD COMPONENT
// ════════════════════════════════════════════════════════════════════════════════

const AccountCard = ({ account, onViewLedger, onEdit, canModify, style }) => {
  const [showMenu, setShowMenu] = useState(false);
  const config = ACCOUNT_TYPE_CONFIG[account.type] || ACCOUNT_TYPE_CONFIG.CASH;
  const Icon = config.icon;

  return (
    <div 
      className="relative bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-slate-300 transition-all cursor-pointer group animate-cardFadeIn"
      style={style}
      onClick={onViewLedger}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-lg`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${config.bg} ${config.text}`}>
            {config.label}
          </span>
          {canModify && (
            <div className="relative">
              <button 
                onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
              >
                <MoreVertical className="w-4 h-4 text-slate-400" />
              </button>
              {showMenu && (
                <div className="absolute right-0 mt-1 w-40 bg-white border border-slate-200 rounded-xl shadow-lg z-10 overflow-hidden animate-modalFadeIn">
                  <button
                    onClick={(e) => { e.stopPropagation(); onViewLedger(); setShowMenu(false); }}
                    className="w-full px-4 py-2.5 text-left text-[13px] text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" /> View Ledger
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onEdit(); setShowMenu(false); }}
                    className="w-full px-4 py-2.5 text-left text-[13px] text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" /> Edit
                  </button>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      try {
                        await cashBankAPI.deleteAccount(account.id);
                        toast.success('Account disabled');
                        window.location.reload();
                      } catch (err) {
                        toast.error(getApiErrorMessage(err, 'Failed to disable account'));
                      }
                    }}
                    className="w-full px-4 py-2.5 text-left text-[13px] text-rose-600 hover:bg-rose-50 flex items-center gap-2"
                  >
                    <Power className="w-4 h-4" /> Disable
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      <p className="text-[13px] text-slate-600 font-medium mb-1">{account.name}</p>
      <p className="text-[22px] font-bold text-slate-800">
        ₹{(account.current_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
      </p>
      
      {account.account_number && (
        <p className="text-[11px] text-slate-400 mt-2">****{account.account_number.slice(-4)}</p>
      )}
      
      <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-teal-300 transition-colors pointer-events-none"></div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// MINI DONUT CHART COMPONENT
// ════════════════════════════════════════════════════════════════════════════════

const MiniDonutChart = ({ segments }) => {
  const total = segments.reduce((sum, s) => sum + s.value, 0) || 1;
  let currentAngle = -90;

  return (
    <div className="flex items-center gap-6">
      <svg width="100" height="100" viewBox="0 0 100 100">
        {segments.map((segment, index) => {
          const percentage = segment.value / total;
          const angle = percentage * 360;
          const startAngle = currentAngle;
          const endAngle = currentAngle + angle;
          currentAngle = endAngle;
          
          const startRad = (startAngle * Math.PI) / 180;
          const endRad = (endAngle * Math.PI) / 180;
          
          const x1 = 50 + 40 * Math.cos(startRad);
          const y1 = 50 + 40 * Math.sin(startRad);
          const x2 = 50 + 40 * Math.cos(endRad);
          const y2 = 50 + 40 * Math.sin(endRad);
          
          const largeArc = angle > 180 ? 1 : 0;
          
          return (
            <path
              key={index}
              d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
              fill={segment.color}
              className="transition-all duration-300 hover:opacity-80"
            />
          );
        })}
        <circle cx="50" cy="50" r="25" fill="white" />
      </svg>
      
      <div className="space-y-2 flex-1">
        {segments.map((segment, idx) => (
          <div key={idx} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: segment.color }}></div>
              <span className="text-[12px] text-slate-600">{segment.label}</span>
            </div>
            <span className="text-[12px] font-semibold text-slate-700">
              {((segment.value / total) * 100).toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// ADD ACCOUNT MODAL - Fixed size, Pastel colors, Inline validation
// ════════════════════════════════════════════════════════════════════════════════

const AccountModal = ({ bid, account, onClose, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: account?.name || '',
    type: account?.type || 'CASH',
    opening_balance: account?.opening_balance || '',
    opening_balance_date: account?.opening_balance_date || new Date().toISOString().split('T')[0],
    bank_name: account?.bank_name || '',
    account_number: account?.account_number || '',
    ifsc_code: account?.ifsc_code || '',
    upi_id: account?.upi_id || '',
    wallet_provider: account?.wallet_provider || ''
  });

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Account name is required';
    if (form.opening_balance && isNaN(parseFloat(form.opening_balance))) {
      newErrors.opening_balance = 'Opening balance must be a number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      await cashBankAPI.createAccount({ ...form, opening_balance: parseFloat(form.opening_balance) || 0 });
      toast.success('Account created successfully!');
      onSuccess();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to create account'));
    } finally { setSaving(false); }
  };

  // Account types with unique labels - no duplicates
  const accountTypes = [
    { key: 'CASH', icon: Banknote, label: 'Cash', gradient: 'from-emerald-400 to-teal-500' },
    { key: 'BANK', icon: Building2, label: 'Bank', gradient: 'from-sky-400 to-blue-500' },
    { key: 'UPI', icon: Smartphone, label: 'UPI', gradient: 'from-violet-400 to-purple-500' },
    { key: 'WALLET', icon: CreditCard, label: 'Wallet', gradient: 'from-rose-400 to-pink-500' },
    { key: 'PETTY_CASH', icon: PiggyBank, label: 'Petty Cash', gradient: 'from-amber-400 to-orange-500' }
  ];

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      {/* Fixed size modal - no resizing */}
      <div className="bg-white rounded-2xl w-full max-w-[520px] h-[600px] flex flex-col shadow-2xl animate-modalFadeIn">
        {/* Fixed Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-[16px] font-semibold text-slate-800">{account ? 'Edit Account' : 'Add New Account'}</h3>
            <p className="text-[12px] text-slate-500 mt-0.5 font-normal">Set up a new fund account</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
        
        {/* Scrollable Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5">
          {/* Account Type Selector - 5 unique types */}
          <div className="mb-5">
            <label className="block text-[12px] text-slate-600 mb-2.5">Account Type</label>
            <div className="grid grid-cols-5 gap-2">
              {accountTypes.map(({ key, icon: Icon, label, gradient }) => (
                <button 
                  key={key} 
                  type="button" 
                  onClick={() => setForm(f => ({ ...f, type: key }))}
                  className={`p-3 rounded-xl border transition-all ${
                    form.type === key 
                      ? 'border-slate-400 bg-slate-50 shadow-sm' 
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center mx-auto mb-1.5 shadow-sm`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <p className={`text-[10px] text-center ${form.type === key ? 'text-slate-700 font-medium' : 'text-slate-500'}`}>
                    {label}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Account Name */}
          <div className="mb-4">
            <label className="block text-[12px] text-slate-600 mb-1.5">Account Name <span className="text-rose-500">*</span></label>
            <input 
              type="text" 
              value={form.name} 
              onChange={(e) => { setForm(f => ({ ...f, name: e.target.value })); setErrors(er => ({ ...er, name: '' })); }}
              placeholder="e.g., Main Cash, HDFC Savings"
              className={`w-full px-3 py-2.5 border rounded-lg text-[13px] focus:ring-2 focus:ring-slate-200 focus:border-slate-400 outline-none transition-all ${errors.name ? 'border-rose-300 bg-rose-50/50' : 'border-slate-200'}`}
            />
            {errors.name && <p className="text-[11px] text-rose-500 mt-1">{errors.name}</p>}
          </div>

          {/* Bank-specific fields - Always rendered but hidden to prevent layout jump */}
          <div className={`space-y-4 mb-4 transition-all duration-200 ${form.type === 'BANK' ? 'opacity-100 max-h-[200px]' : 'opacity-0 max-h-0 overflow-hidden'}`}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[12px] text-slate-600 mb-1.5">Bank Name</label>
                <input 
                  type="text" 
                  value={form.bank_name} 
                  onChange={(e) => setForm(f => ({ ...f, bank_name: e.target.value }))} 
                  placeholder="e.g., HDFC Bank"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-slate-200 focus:border-slate-400 outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-[12px] text-slate-600 mb-1.5">Account Number</label>
                <input 
                  type="text" 
                  value={form.account_number} 
                  onChange={(e) => setForm(f => ({ ...f, account_number: e.target.value }))} 
                  placeholder="****1234"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-slate-200 focus:border-slate-400 outline-none transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-[12px] text-slate-600 mb-1.5">IFSC Code</label>
              <input 
                type="text" 
                value={form.ifsc_code} 
                onChange={(e) => setForm(f => ({ ...f, ifsc_code: e.target.value }))} 
                placeholder="e.g., HDFC0001234"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-slate-200 focus:border-slate-400 outline-none transition-all"
              />
            </div>
          </div>

          {/* UPI-specific fields */}
          <div className={`mb-4 transition-all duration-200 ${form.type === 'UPI' ? 'opacity-100 max-h-[100px]' : 'opacity-0 max-h-0 overflow-hidden'}`}>
            <label className="block text-[12px] text-slate-600 mb-1.5">UPI ID</label>
            <input 
              type="text" 
              value={form.upi_id} 
              onChange={(e) => setForm(f => ({ ...f, upi_id: e.target.value }))} 
              placeholder="yourname@upi"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-slate-200 focus:border-slate-400 outline-none transition-all"
            />
          </div>

          {/* Wallet-specific fields */}
          <div className={`mb-4 transition-all duration-200 ${form.type === 'WALLET' ? 'opacity-100 max-h-[100px]' : 'opacity-0 max-h-0 overflow-hidden'}`}>
            <label className="block text-[12px] text-slate-600 mb-1.5">Wallet Provider</label>
            <input 
              type="text" 
              value={form.wallet_provider}
              onChange={(e) => setForm(f => ({ ...f, wallet_provider: e.target.value }))}
              placeholder="e.g., Paytm, PhonePe"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-slate-200 focus:border-slate-400 outline-none transition-all"
            />
          </div>

          {/* Opening Balance */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-[12px] text-slate-600 mb-1.5">Opening Balance</label>
              <input 
                type="number" 
                value={form.opening_balance} 
                onChange={(e) => { setForm(f => ({ ...f, opening_balance: e.target.value })); setErrors(er => ({ ...er, opening_balance: '' })); }}
                placeholder="0.00"
                className={`w-full px-3 py-2.5 border rounded-lg text-[13px] focus:ring-2 focus:ring-slate-200 focus:border-slate-400 outline-none transition-all ${errors.opening_balance ? 'border-rose-300 bg-rose-50/50' : 'border-slate-200'}`}
              />
              {errors.opening_balance && <p className="text-[11px] text-rose-500 mt-1">{errors.opening_balance}</p>}
            </div>
            <div>
              <label className="block text-[12px] text-slate-600 mb-1.5">As of Date</label>
              <input 
                type="date" 
                value={form.opening_balance_date} 
                onChange={(e) => setForm(f => ({ ...f, opening_balance_date: e.target.value }))} 
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-slate-200 focus:border-slate-400 outline-none transition-all"
              />
            </div>
          </div>
        </form>

        {/* Fixed Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-slate-100 flex gap-3">
          <button 
            type="button" 
            onClick={onClose} 
            className="flex-1 py-2.5 border border-slate-200 rounded-lg text-[13px] text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 py-2.5 bg-slate-800 text-white rounded-lg text-[13px] font-medium hover:bg-slate-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
          >
            {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Creating...</> : 'Create Account'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// TRANSFER MODAL - Fixed size, Pastel colors, Premium styling
// ════════════════════════════════════════════════════════════════════════════════

const TransferModal = ({ bid, accounts, onClose, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    from_account_id: '',
    to_account_id: '',
    amount: '',
    description: '',
    transfer_date: new Date().toISOString().split('T')[0]
  });
  
  const fromAccount = accounts.find(a => a.id === form.from_account_id);

  const validateForm = () => {
    const newErrors = {};
    if (!form.from_account_id) newErrors.from_account_id = 'Select source account';
    if (!form.to_account_id) newErrors.to_account_id = 'Select destination account';
    if (!form.amount) newErrors.amount = 'Amount is required';
    if (form.amount && isNaN(parseFloat(form.amount))) newErrors.amount = 'Amount must be a number';
    if (form.from_account_id === form.to_account_id && form.from_account_id) newErrors.to_account_id = 'Must be different from source';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      await cashBankAPI.transfer({ ...form, amount: parseFloat(form.amount) });
      toast.success('Transfer completed successfully!');
      onSuccess();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Transfer failed'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl w-full max-w-[440px] h-[520px] flex flex-col shadow-2xl animate-modalFadeIn">
        {/* Fixed Header - Pastel blue */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-sky-100 flex items-center justify-between bg-gradient-to-r from-sky-50 to-blue-50 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center shadow-sm">
              <ArrowRightLeft className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-[16px] font-semibold text-slate-800">Transfer Funds</h3>
              <p className="text-[11px] text-slate-500 font-normal">Move money between accounts</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/60 rounded-lg transition-colors">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
        
        {/* Scrollable Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5">
          <div className="mb-4">
            <label className="block text-[12px] text-slate-600 mb-1.5">From Account <span className="text-rose-500">*</span></label>
            <select 
              value={form.from_account_id} 
              onChange={(e) => { setForm(f => ({ ...f, from_account_id: e.target.value })); setErrors(er => ({ ...er, from_account_id: '' })); }}
              className={`w-full px-3 py-2.5 border rounded-lg text-[13px] focus:ring-2 focus:ring-slate-200 focus:border-slate-400 outline-none ${errors.from_account_id ? 'border-rose-300 bg-rose-50/50' : 'border-slate-200'}`}
            >
              <option value="">Select source account</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{a.name} (₹{a.current_balance?.toLocaleString()})</option>
              ))}
            </select>
            {errors.from_account_id && <p className="text-[11px] text-rose-500 mt-1">{errors.from_account_id}</p>}
          </div>
          
          <div className="flex justify-center my-3">
            <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center">
              <ArrowRightLeft className="w-4 h-4 text-sky-600" />
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-[12px] text-slate-600 mb-1.5">To Account <span className="text-rose-500">*</span></label>
            <select 
              value={form.to_account_id} 
              onChange={(e) => { setForm(f => ({ ...f, to_account_id: e.target.value })); setErrors(er => ({ ...er, to_account_id: '' })); }}
              className={`w-full px-3 py-2.5 border rounded-lg text-[13px] focus:ring-2 focus:ring-slate-200 focus:border-slate-400 outline-none ${errors.to_account_id ? 'border-rose-300 bg-rose-50/50' : 'border-slate-200'}`}
            >
              <option value="">Select destination account</option>
              {accounts.filter(a => a.id !== form.from_account_id).map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            {errors.to_account_id && <p className="text-[11px] text-rose-500 mt-1">{errors.to_account_id}</p>}
          </div>
          
          <div className="mb-4">
            <label className="block text-[12px] text-slate-600 mb-1.5">Amount <span className="text-rose-500">*</span></label>
            <input 
              type="number" 
              value={form.amount} 
              onChange={(e) => { setForm(f => ({ ...f, amount: e.target.value })); setErrors(er => ({ ...er, amount: '' })); }}
              placeholder="0.00"
              className={`w-full px-3 py-2.5 border rounded-lg text-[13px] focus:ring-2 focus:ring-slate-200 focus:border-slate-400 outline-none ${errors.amount ? 'border-rose-300 bg-rose-50/50' : 'border-slate-200'}`}
            />
            {errors.amount && <p className="text-[11px] text-rose-500 mt-1">{errors.amount}</p>}
            {fromAccount && !errors.amount && (
              <p className="text-[11px] text-slate-400 mt-1">Available: ₹{fromAccount.current_balance?.toLocaleString()}</p>
            )}
          </div>
          
          <div className="mb-4">
            <label className="block text-[12px] text-slate-600 mb-1.5">Notes (Optional)</label>
            <input 
              type="text" 
              value={form.description} 
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} 
              placeholder="Transfer description"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-slate-200 focus:border-slate-400 outline-none"
            />
          </div>
        </form>

        {/* Fixed Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-slate-100 flex gap-3">
          <button 
            type="button" 
            onClick={onClose} 
            className="flex-1 py-2.5 border border-slate-200 rounded-lg text-[13px] text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg text-[13px] font-medium hover:shadow-lg disabled:opacity-50 transition-all flex items-center justify-center gap-2"
          >
            {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Transferring...</> : 'Transfer Funds'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// ADJUST BALANCE MODAL - Fixed size, Pastel colors, Inline validation
// ════════════════════════════════════════════════════════════════════════════════

const AdjustModal = ({ bid, accounts, onClose, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    account_id: '',
    action: 'add',
    amount: '',
    reason: '',
    txn_date: new Date().toISOString().split('T')[0]
  });

  const validateForm = () => {
    const newErrors = {};
    if (!form.account_id) newErrors.account_id = 'Select an account';
    if (!form.amount) newErrors.amount = 'Amount is required';
    if (form.amount && isNaN(parseFloat(form.amount))) newErrors.amount = 'Amount must be a number';
    if (!form.reason) newErrors.reason = 'Reason is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      await cashBankAPI.adjust({ ...form, amount: parseFloat(form.amount) });
      toast.success(form.action === 'add' ? 'Money added successfully!' : 'Money reduced successfully!');
      onSuccess();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Operation failed'));
    } finally { setSaving(false); }
  };

  const addReasons = ['Owner added cash', 'Cash correction', 'Other income', 'Capital investment'];
  const reduceReasons = ['Owner withdrew cash', 'Cash correction', 'Cash shortage', 'Expense adjustment'];

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl w-full max-w-[440px] h-[520px] flex flex-col shadow-2xl animate-modalFadeIn">
        {/* Fixed Header - Pastel tones */}
        <div className={`flex-shrink-0 px-6 py-4 border-b flex items-center justify-between rounded-t-2xl transition-colors duration-300 ${
          form.action === 'add' 
            ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-100' 
            : 'bg-gradient-to-r from-rose-50 to-pink-50 border-rose-100'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shadow-sm ${
              form.action === 'add' 
                ? 'bg-gradient-to-br from-emerald-400 to-teal-500' 
                : 'bg-gradient-to-br from-rose-400 to-pink-500'
            }`}>
              {form.action === 'add' ? (
                <ArrowDownLeft className="w-4 h-4 text-white" />
              ) : (
                <ArrowUpRight className="w-4 h-4 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-[16px] font-semibold text-slate-800">Adjust Balance</h3>
              <p className="text-[11px] text-slate-500 font-normal">Add or reduce account balance</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/60 rounded-lg transition-colors">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
        
        {/* Scrollable Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5">
          <div className="mb-4">
            <label className="block text-[12px] text-slate-600 mb-1.5">Account <span className="text-rose-500">*</span></label>
            <select 
              value={form.account_id} 
              onChange={(e) => { setForm(f => ({ ...f, account_id: e.target.value })); setErrors(er => ({ ...er, account_id: '' })); }}
              className={`w-full px-3 py-2.5 border rounded-lg text-[13px] focus:ring-2 focus:ring-slate-200 focus:border-slate-400 outline-none ${errors.account_id ? 'border-rose-300 bg-rose-50/50' : 'border-slate-200'}`}
            >
              <option value="">Select account</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{a.name} (₹{a.current_balance?.toLocaleString()})</option>
              ))}
            </select>
            {errors.account_id && <p className="text-[11px] text-rose-500 mt-1">{errors.account_id}</p>}
          </div>

          {/* Action Toggle - Smaller, pastel */}
          <div className="mb-4">
            <label className="block text-[12px] text-slate-600 mb-2">Action <span className="text-rose-500">*</span></label>
            <div className="grid grid-cols-2 gap-2">
              <button 
                type="button" 
                onClick={() => setForm(f => ({ ...f, action: 'add', reason: '' }))}
                className={`p-3 rounded-lg border flex items-center justify-center gap-2 transition-all ${
                  form.action === 'add' 
                    ? 'border-emerald-300 bg-emerald-50 shadow-sm' 
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className={`w-7 h-7 rounded-md ${form.action === 'add' ? 'bg-gradient-to-br from-emerald-400 to-teal-500' : 'bg-slate-200'} flex items-center justify-center`}>
                  <ArrowDownLeft className={`w-3.5 h-3.5 ${form.action === 'add' ? 'text-white' : 'text-slate-500'}`} />
                </div>
                <span className={`text-[12px] ${form.action === 'add' ? 'text-emerald-700 font-medium' : 'text-slate-600'}`}>Add Money</span>
              </button>
              <button 
                type="button" 
                onClick={() => setForm(f => ({ ...f, action: 'reduce', reason: '' }))}
                className={`p-3 rounded-lg border flex items-center justify-center gap-2 transition-all ${
                  form.action === 'reduce' 
                    ? 'border-rose-300 bg-rose-50 shadow-sm' 
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className={`w-7 h-7 rounded-md ${form.action === 'reduce' ? 'bg-gradient-to-br from-rose-400 to-pink-500' : 'bg-slate-200'} flex items-center justify-center`}>
                  <ArrowUpRight className={`w-3.5 h-3.5 ${form.action === 'reduce' ? 'text-white' : 'text-slate-500'}`} />
                </div>
                <span className={`text-[12px] ${form.action === 'reduce' ? 'text-rose-700 font-medium' : 'text-slate-600'}`}>Reduce</span>
              </button>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-[12px] text-slate-600 mb-1.5">Amount <span className="text-rose-500">*</span></label>
            <input 
              type="number" 
              value={form.amount} 
              onChange={(e) => { setForm(f => ({ ...f, amount: e.target.value })); setErrors(er => ({ ...er, amount: '' })); }}
              placeholder="0.00"
              className={`w-full px-3 py-2.5 border rounded-lg text-[13px] focus:ring-2 focus:ring-slate-200 focus:border-slate-400 outline-none ${errors.amount ? 'border-rose-300 bg-rose-50/50' : 'border-slate-200'}`}
            />
            {errors.amount && <p className="text-[11px] text-rose-500 mt-1">{errors.amount}</p>}
          </div>

          <div className="mb-4">
            <label className="block text-[12px] text-slate-600 mb-1.5">Reason <span className="text-rose-500">*</span></label>
            <select 
              value={form.reason} 
              onChange={(e) => { setForm(f => ({ ...f, reason: e.target.value })); setErrors(er => ({ ...er, reason: '' })); }}
              className={`w-full px-3 py-2.5 border rounded-lg text-[13px] focus:ring-2 focus:ring-slate-200 focus:border-slate-400 outline-none ${errors.reason ? 'border-rose-300 bg-rose-50/50' : 'border-slate-200'}`}
            >
              <option value="">Select reason</option>
              {(form.action === 'add' ? addReasons : reduceReasons).map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            {errors.reason && <p className="text-[11px] text-rose-500 mt-1">{errors.reason}</p>}
          </div>
        </form>

        {/* Fixed Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-slate-100 flex gap-3">
          <button 
            type="button" 
            onClick={onClose} 
            className="flex-1 py-2.5 border border-slate-200 rounded-lg text-[13px] text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            disabled={saving}
            className={`flex-1 py-2.5 text-white rounded-lg text-[13px] font-medium disabled:opacity-50 transition-all hover:shadow-lg flex items-center justify-center gap-2 ${
              form.action === 'add' 
                ? 'bg-gradient-to-r from-emerald-500 to-teal-600' 
                : 'bg-gradient-to-r from-rose-500 to-pink-600'
            }`}
          >
            {saving ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Processing...</>
            ) : form.action === 'add' ? (
              'Add Money'
            ) : (
              'Reduce Money'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// LEDGER MODAL
// ════════════════════════════════════════════════════════════════════════════════

const LedgerModal = ({ data, onClose }) => {
  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  const config = ACCOUNT_TYPE_CONFIG[data.account?.type] || ACCOUNT_TYPE_CONFIG.CASH;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl animate-modalFadeIn">
        {/* Header */}
        <div className={`px-6 py-5 flex items-center justify-between text-white bg-gradient-to-r ${config.gradient}`}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              {React.createElement(config.icon, { className: "w-6 h-6 text-white" })}
            </div>
            <div>
              <h3 className="text-[18px] font-bold">{data.account?.name}</h3>
              <p className="text-[13px] text-white/80">Account Ledger</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Summary Cards */}
        <div className="px-6 py-5 bg-slate-50 grid grid-cols-4 gap-4 border-b border-slate-100">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-[11px] text-slate-500 uppercase tracking-wide mb-1">Opening Balance</p>
            <p className="text-[18px] font-bold text-slate-800">{formatCurrency(data.summary?.opening_balance)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-[11px] text-slate-500 uppercase tracking-wide mb-1">Total Money In</p>
            <p className="text-[18px] font-bold text-emerald-600">{formatCurrency(data.summary?.total_money_in)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-[11px] text-slate-500 uppercase tracking-wide mb-1">Total Money Out</p>
            <p className="text-[18px] font-bold text-rose-600">{formatCurrency(data.summary?.total_money_out)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-[11px] text-slate-500 uppercase tracking-wide mb-1">Closing Balance</p>
            <p className="text-[18px] font-bold text-blue-600">{formatCurrency(data.summary?.closing_balance)}</p>
          </div>
        </div>

        {/* Ledger Table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Description</th>
                <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Money In</th>
                <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Money Out</th>
                <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.ledger?.map((entry, idx) => (
                <tr key={entry.id || idx} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-5 py-3 text-[13px] text-slate-600">{entry.date}</td>
                  <td className="px-5 py-3 text-[13px] text-slate-800">{entry.description}</td>
                  <td className="px-5 py-3 text-[13px] text-right font-semibold text-emerald-600">
                    {entry.money_in > 0 ? formatCurrency(entry.money_in) : '—'}
                  </td>
                  <td className="px-5 py-3 text-[13px] text-right font-semibold text-rose-600">
                    {entry.money_out > 0 ? formatCurrency(entry.money_out) : '—'}
                  </td>
                  <td className="px-5 py-3 text-[13px] text-right font-bold text-slate-800">{formatCurrency(entry.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50">
          <button 
            onClick={onClose} 
            className="px-5 py-2.5 bg-slate-200 hover:bg-slate-300 rounded-xl text-[14px] font-semibold text-slate-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FundsAccountsPage;
